<template>
  <div>
    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ loadingError }}
        </p>
      </div>
    </b-alert>
    <b-row>
      <b-col
        align="right"
      >
        <b-button
          variant="outline-primary"
          class="mb-1 ml-1"
          :disabled="deactivating || selectedId.length < 1"
          @click="deactivateHandler"
        >
          Deactivate
        </b-button>
        <b-button
          variant="outline-primary"
          class="mb-1 ml-2"
          @click="openAddMinimumQuantityForm"
        >
          Add Minimum Quantity
        </b-button>
      </b-col>
    </b-row>

    <b-table
      v-if="!loading && !loadingError"
      :items="results"
      :fields="tableFields"
      responsive="sm"
      striped
      hover
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      class="compact-table"
      no-local-sorting
      @sort-changed="sortingChanged"
    >
      <template #head(select)>
        <b-form-checkbox
          v-model="isAllIdSelected"
          :disabled="results.length === 0"
          @change="handleAllIdSelectChange"
        />
      </template>
      <template #cell(select)="value">
        <b-form-checkbox
          v-model="selectedId"
          :value="value.item.id"
        />
      </template>
      <template #cell(is_active)="{ item }">
        <app-chip :variant="item.minimum_order_quantity_applicable ? 'success' : 'secondary'">
          {{ item.minimum_order_quantity_applicable ? 'Active' : 'Inactive' }}
        </app-chip>
      </template>
      <template #cell(action)="data">
        <feather-icon
          v-b-tooltip.hover="{boundary:'window'}"
          title="Edit Minimum Quantity"
          icon="EditIcon"
          size="20"
          class="cursor-pointer mr-1"
          @click="openEditMinimumQuantityForm(data.item.id)"
        />
      </template>
    </b-table>
    <detailed-pagination
      v-if="!loading && !loadingError"
      :total-items="totalItems"
      :per-page="perPage"
      :current-page="currentPage"
      @page-changed="(page) => {currentPage = page}"
    />
    <add-minimum-quantity
      v-if="showMinimumQuantityForm"
      :id="selectedMinimumQuantityId"
      @modal-closed="closeMinimumQuantityForm"
      @minimum-quantity-added="handleMinimumQuantityAdded"
      @minimum-quantity-updated="handleMinimumQuantityUpdated"
    />
  </div>
</template>

<script>
import {
  BSpinner, BTable, BAlert, BRow, BCol, BButton, VBTooltip, BFormCheckbox,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue'
import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import getParsedQueryString from '@/utils/headerQueryParser'
import AppChip from '@/components/UI/AppChip.vue'
import DetailedPagination from '../../UI/DetailedPagination.vue'
import AddMinimumQuantity from './AddMinimumQuantity.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BSpinner,
    BTable,
    BRow,
    BCol,
    BButton,
    BAlert,
    AppChip,
    BFormCheckbox,
    DetailedPagination,
    AddMinimumQuantity,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      deactivating: false,
      loadingError: null,
      results: [],
      tableFields: [
        { key: 'select' },
        { key: 'id', label: 'ID', sortable: true },
        { key: 'product_name', label: 'Product Name', sortable: true },
        { key: 'company', label: 'Company', sortable: true },
        { key: 'mrp', label: 'MRP', sortable: true },
        { key: 'box_qty', label: 'Box Quantity', sortable: true },
        { key: 'lot', label: 'Lot', sortable: true },
        { key: 'minimum_order_quantity', label: 'Minimum Quantity', sortable: true },
        { key: 'is_active', label: 'Status', sortable: true },
        { key: 'action', label: 'Action', sortable: false },
      ],
      isAllIdSelected: false,
      selectedId: [],
      selectedMinimumQuantityId: null,
      currentPage: 1,
      perPage: 50,
      totalItems: 0,
      sortBy: 'created_at',
      sortDesc: true,
      submitting: false,
      showMinimumQuantityForm: false,
    }
  },
  watch: {
    currentPage() {
      this.fetchItems()
    },
    filters() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.fetchItems()
      }
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchItems()
    },

    fetchItems() {
      const params = {
        sort_by: this.sortBy,
        sort_desc: this.sortDesc,
        page: this.currentPage,
        per_page: this.perPage,
        ...this.filters,
      }
      this.loading = true
      axios.get(`/marg/product_controls/?${getParsedQueryString(params)}`)
        .then(res => {
          this.results = res.data.results
          this.totalItems = res.data.count
          this.loadingError = null
          this.loading = false
        }).catch(error => {
          this.loadingError = error?.response?.data?.error || 'Something went wrong'
          this.loading = false
        })
    },
    async deactivateHandler() {
      this.deactivating = true

      // Process orders one by one using reduce
      try {
        await axios.post('/marg/bulk_update_product_controls/', { ids: this.selectedId, minimum_order_quantity_applicable: false })

        // Show individual success toast for each order
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Status Updated',
            text: `Total  ${this.selectedId.length} bonus status deactivated successfully`,
            variant: 'success',
          },
        })

        this.deactivating = false

        // Clear selected Bonuses
        this.selectedId = []
        this.isAllIdSelected = false

        // Refresh the data
        this.fetchItems()
      } catch (error) {
        this.deactivating = false

        // Show error toast
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Status Update Failed',
            text: error?.response?.data?.error || error?.response?.data?.detail || 'Something went wrong while placing orders',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
    openAddMinimumQuantityForm() {
      this.selectedMinimumQuantityId = null
      this.showMinimumQuantityForm = true
    },

    openEditMinimumQuantityForm(mimimimQuantityId) {
      this.selectedMinimumQuantityId = mimimimQuantityId
      this.showMinimumQuantityForm = true
    },

    closeMinimumQuantityForm() {
      this.showMinimumQuantityForm = false
      this.selectedMinimumQuantityId = null
    },

    handleMinimumQuantityAdded() {
      this.closeMinimumQuantityForm()
      this.currentPage = 1
      this.fetchItems()
    },

    handleMinimumQuantityUpdated() {
      this.closeMinimumQuantityForm()
      this.fetchItems()
    },
    handleAllIdSelectChange(isChecked) {
      this.selectedId = isChecked
        ? this.results.map(data => data.id)
        : []
    },
  },
}
</script>

<style scoped>
.text-success {
  font-size: 1.2rem;
  font-weight: bold;
}
</style>
